import { env } from '../config/env';
import { API_HEADERS } from '../constants/apiHeaders';
import api from '../utils/api';
import { APPLICANT_ENDPOINTS } from './endpoints/rec/applicant.endpoints';
import { RESUME_ENDPOINTS } from './endpoints/rec/resume.endpoints';
import { VACANCY_ENDPOINTS } from './endpoints/rec/vacancy.endpoints';
import { SYSTEM_ENDPOINTS } from './endpoints/system.endpoints';
import type { ApplicantResponse } from '../components/common/table/types';
import type { ApplicantMessage } from './types/applicant.types';

export type FetchApplicantsParams = {
  status?: string | null;
  source?: string | null;
  vacancyCode?: string | null;
  vacancyOrName?: string | null;
  email?: string | null;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
  sort?: string;
};

export const applicantService = {
  search: (params: FetchApplicantsParams) =>
    api.get<ApplicantResponse>(APPLICANT_ENDPOINTS.search, {
      params,
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  getFeedback: (applicantId: number) =>
    api.get(APPLICANT_ENDPOINTS.feedback(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  generateCVComparisonFeedback: (applicantId: number) =>
    api.get(APPLICANT_ENDPOINTS.generateCVComparisonFeedback(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_SEARCH },
    }),

  generateCvEmail: (applicantId: number, email: string) =>
    api.get(`${RESUME_ENDPOINTS.generateApplicantCv}?id=${applicantId}&email=${email}`, {
      headers: { apikey: env.cvApiKey },
    }),

  getVacancyCodes: () =>
    api.get<{ data: { vacancy_Code: string; value: string }[] }>(VACANCY_ENDPOINTS.vacancyCodes, {
      headers: { ...API_HEADERS.VACANCY_CODES_LOOKUP },
    }),

  getCountries: () =>
    api.get<{ data: { id: number; name: string; currency: string }[] }>(SYSTEM_ENDPOINTS.countries, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_COUNTRIES },
    }),

  updateVacancyCode: (payload: { applicantId: number; issueId: string | null; vacancyCode: string }) =>
    api.put(APPLICANT_ENDPOINTS.updateVacancyCode, payload, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_UPDATE_VACANCY_CODE },
    }),

  updateCountry: (applicantId: number, payload: { countryId: number }) =>
    api.put(APPLICANT_ENDPOINTS.updateCountry(applicantId), payload, {
      headers: { ...API_HEADERS.APPLICANT_SEARCH_UPDATE_COUNTRY },
    }),

  addApplicant: (formData: FormData) =>
    api.post(APPLICANT_ENDPOINTS.add, formData, {
      headers: {
        ...API_HEADERS.ADD_APPLICANT_SUBMIT,
        'Content-Type': 'multipart/form-data',
      },
    }),

  getJiraComments: <T = { comments: unknown[] }>(applicantId: string) =>
    api.get<T>(APPLICANT_ENDPOINTS.jiraComments(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_JIRA_COMMENTS },
    }),

  getJiraAttachmentBlob: (issueId: string, filename: string) =>
    api.get<Blob>(APPLICANT_ENDPOINTS.jiraAttachment(issueId, filename), {
      headers: { ...API_HEADERS.APPLICANT_JIRA_COMMENTS },
      responseType: 'blob',
    }),

  getFullProfile: <T = unknown>(applicantId: string) =>
    api.get<T>(APPLICANT_ENDPOINTS.byId(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_PROFILE_FULL_GET },
    }),

  updateProfile: (applicantId: string, payload: unknown) =>
    api.put(APPLICANT_ENDPOINTS.updateProfile(applicantId), payload, {
      headers: { ...API_HEADERS.APPLICANT_PROFILE_UPDATE },
    }),

  getMessages: (applicantId: string) =>
    api.get<ApplicantMessage[]>(APPLICANT_ENDPOINTS.messages(applicantId), {
      headers: { ...API_HEADERS.APPLICANT_MESSAGES_LIST },
    }),

  testSendMessage: (applicantId: number, message: string) =>
    api.post(APPLICANT_ENDPOINTS.testSend, { applicantId, message }, {
      headers: { ...API_HEADERS.SEND_MESSAGE_SEND },
    }),
};
